import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  useTheme,
  Button,
  Typography,
  Grid,
  Card,
  darken,
  IconButton,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { lighten } from "@material-ui/core";

import { setAlert } from "../features/slices/alertSlice";
import { selectCurrentToken } from "../features/slices/authSlice";
import { openModal, closeModal } from "../features/slices/modalSlice";

import Image from "../components/common/Image";
import Backdrop from "../components/common/Backdrop";
import DOMPurify from "dompurify";
import CopiedTooltip from "../components/common/CopiedTooltip";

import {
  useLazyGetBonusListQuery,
  useLazyGetBonusDetailsQuery,
  useClaimBonusMutation,
} from "../features/api/bonusApiSlice";
import {
  useLazyGetPublicBonusListQuery,
  useLazyGetPublicBonusDetailsQuery,
  useGetWhatsappGroupQuery,
} from "../features/api/publicApiSlice";
import { useGetShareLinkQuery } from "../features/api/userApiSlice";
import { useGetGameArticlesQuery } from "../features/api/homeApiSlice";
import * as Constant from "../features/constant";

const SIDEBAR_W = { xs: 58, md: 100 };

const Bonus_v2 = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const token = useSelector(selectCurrentToken);

  const lighter = lighten(theme.palette.primary.main, 0.1);
  const darker = darken(theme.palette.primary.main, 0.05);
  const PRIMARY = theme.palette.primary.main;
  const PAPER = theme.palette.background.paper;
  const TEXT = process.env.REACT_APP_COLOR_TEXT || "#333";
  const MUTED = "#888";
  const DIVIDER = process.env.REACT_APP_COLOR_DIVIDER1 || "#e0e0e0";

  // Redemption code shows "Join Now" (chat group) instead of Claim/Register. Link is
  // config-backed so marketing can change it without a redeploy.
  const { data: whatsappGroup } = useGetWhatsappGroupQuery();
  const whatsappGroupUrl = whatsappGroup?.result?.value;
  const REDEMPTION_CODE = "redemption_code";

  // ── State ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [bonusCategory, setBonusCategory] = useState("ALL");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isClaimTrigger, setIsClaimTrigger] = useState(false);
  const [bonusType, setBonusType] = useState(null);
  const [isBonusSelected, setIsBonusSelected] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const pendingBonusCodeRef = useRef(null);

  // ── API ───────────────────────────────────────────────────────────────────
  const [getBonusList, { data: bonusList }] = useLazyGetBonusListQuery({ prefetch: true });
  const [
    getPublicBonusList,
    { data: publicBonusList, isSuccess: isPublicBonusListSuccess },
  ] = useLazyGetPublicBonusListQuery({ prefetch: true });
  const [
    getPublicBonusDetails,
    { data: publicBonusDetails, isLoading: isPublicBonusDetailsLoading, isSuccess: isPublicBonusDetailsSuccess },
  ] = useLazyGetPublicBonusDetailsQuery({ prefetch: true });
  const [
    getBonusDetails,
    { data: bonusDetails, isLoading: isBonusDetailsLoading, isSuccess: isBonusDetailsSuccess },
  ] = useLazyGetBonusDetailsQuery({ prefetch: true });
  const [
    claim,
    { isLoading: isClaimBonusLoading, isFetching: isClaimBonusFetching, isError: isClaimBonusError, isSuccess: isClaimBonusSuccess },
  ] = useClaimBonusMutation({ prefetch: true });
  const { data: shareLink } = useGetShareLinkQuery();
  const { data: gameArticles } = useGetGameArticlesQuery();

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    getPublicBonusList(bonusCategory);
    getBonusList(bonusCategory);
  }, [bonusCategory]);

  useEffect(() => {
    const list = gameArticles?.result;
    if (list?.length > 0 && !selectedArticle) setSelectedArticle(list[0]);
  }, [gameArticles]);

  useEffect(() => {
    if (isClaimBonusError) dispatch(openModal({ title: isClaimBonusError?.data?.message }));
  }, [isClaimBonusError]);

  useEffect(() => {
    if (!isClaimBonusLoading && isClaimBonusSuccess) {
      dispatch(setAlert({ isOpen: true, message: t("promotion.successmessage"), variant: "success" }));
      dispatch(closeModal());
    }
  }, [isClaimBonusLoading, isClaimBonusSuccess, isClaimBonusError]);

  useEffect(() => {
    const all = document.querySelectorAll('[id^="button-"]');
    all.forEach((b) => { b.style.border = "6px solid transparent"; });
    const sel = document.getElementById(`button-${selectedItem?.bonusCode}`);
    if (sel) sel.style.border = `6px solid ${PRIMARY}`;
  }, [selectedItem]);

  useEffect(() => {
    if (!openDetailsModal && !isClaimTrigger) return;
    const requestedCode = pendingBonusCodeRef.current;
    const loadedCode = publicBonusDetails?.result?.bonusCode || bonusDetails?.result?.bonusCode;
    if (requestedCode && loadedCode && loadedCode !== requestedCode) return;
    if (
      (!isBonusDetailsLoading && isBonusDetailsSuccess) ||
      (!isPublicBonusDetailsLoading && isPublicBonusDetailsSuccess)
    ) {
      const title = publicBonusDetails?.result?.displayTitle || bonusDetails?.result?.displayTitle;
      if (isClaimTrigger) {
        dispatch(openModal({
          title,
          content: (
            <>
              {(bonusList || publicBonusList)?.result?.bonus?.map((item, i) => {
                if (item.bonusType !== bonusType) return null;
                const isSel = selectedItem === item;
                return (
                  <Grid item xs={1} sm={1} md={1} key={i}>
                    <Button id={`button-${item?.bonusCode}`} variant="text"
                      onClick={() => { const n = isSel ? null : item; setSelectedItem(n); setIsBonusSelected(!!n); }}
                      sx={{ textDecoration: "none", color: "inherit", border: isSel ? PRIMARY : "6px solid transparent", "&:hover": { border: isSel ? PRIMARY : "6px solid transparent" }, padding: "0" }}
                    >
                      <Card variant="outlined" sx={{ bgcolor: PAPER, borderWidth: 0, padding: "0" }}>
                        <Image src={item.thumbnailUrl} alt={Constant.ALT_TAG_BONUS_IMG} />
                      </Card>
                    </Button>
                  </Grid>
                );
              })}
            </>
          ),
          button: !token
            ? [
                { label: t("promotion.login"), onSubmit: () => { dispatch(closeModal()); navigate({ pathname: location.pathname, search: "?login=1" }); } },
                { label: t("promotion.register"), onSubmit: () => { dispatch(closeModal()); navigate({ pathname: location.pathname, search: "?register=1" }); } },
              ]
            : token && bonusDetails?.result?.claimable
            ? [{ label: t("promotion.claim"), onSubmit: () => { dispatch(closeModal()); handleClaimBonus(selectedItem?.bonusCode); }, disabled: !selectedItem?.isActivated }]
            : [],
        }));
      } else {
        setIsClaimTrigger(false);
        setOpenDetailsModal(false);
        dispatch(openModal({
          title,
          content: (
            <>
              <Image
                src={(publicBonusDetails?.result?.bonusTypeThumbnailUrl || publicBonusDetails?.result?.thumbnailUrl) || (bonusDetails?.result?.bonusTypeThumbnailUrl || bonusDetails?.result?.thumbnailUrl)}
                sx={{ width: "80%" }}
                alt={Constant.ALT_TAG_BONUS_IMG}
              />
              <div style={{color: TEXT}} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(publicBonusDetails?.result?.content || bonusDetails?.result?.content) }} />
              {token && loadedCode === "B044" && (
                <div style={{ display: "flex", justifyContent: "center", position: "absolute", bottom: 0, width: "100%", padding: "20px", background: process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || PAPER, left: 0 }}>
                  <TextField
                    label={t("walletcard.myaccountpage.referrallink")} value={shareLink?.result?.url}
                    InputLabelProps={{ shrink: true }} disabled type="text" size="small" sx={{ width: "400px" }}
                    InputProps={{ endAdornment: <InputAdornment position="end"><IconButton disableRipple sx={{ "&.MuiButtonBase-root:hover": { backgroundColor: "transparent" } }}><CopiedTooltip text={shareLink?.result?.url} /></IconButton></InputAdornment> }}
                  />
                </div>
              )}
            </>
          ),
          // Redemption code overrides the usual login/register (or empty) footer with a
          // single Join Us action pointing at the chat group, regardless of login.
          button: loadedCode === REDEMPTION_CODE
            ? [
                {
                  label: t("promotion.joinnow") !== "promotion.joinnow" ? t("promotion.joinnow") : "Join Now",
                  onSubmit: () => {
                    if (whatsappGroupUrl) window.open(whatsappGroupUrl, "_blank", "noopener,noreferrer");
                    dispatch(closeModal());
                  },
                },
              ]
            : !token
            ? [
                { label: t("promotion.login"), onSubmit: () => { dispatch(closeModal()); navigate({ pathname: location.pathname, search: "?login=1" }); } },
                { label: t("promotion.register"), onSubmit: () => { dispatch(closeModal()); navigate({ pathname: location.pathname, search: "?register=1" }); } },
              ]
            : [],
        }));
      }
    }
  }, [bonusDetails, publicBonusDetails, openDetailsModal, token, selectedItem]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleClaimBonus = async (bonusCode) => { await claim({ bonusCode }).unwrap(); };

  const handleReadMore = (list) => {
    pendingBonusCodeRef.current = list.bonusCode;
    getBonusDetails(list.bonusCode);
    getPublicBonusDetails(list.bonusCode);
    setIsClaimTrigger(false);
    setOpenDetailsModal(true);
  };

  const handleClaim = (list) => {
    if (token && bonusList?.result && ["CUSTOM", "REFERRAL", "DEPOSIT", "RESCUE", "RESCUE_ESPORT"].includes(list.bonusType)) {
      handleClaimBonus(list?.bonusCode);
    } else if (token && bonusList?.result) {
      getBonusDetails(list.bonusCode);
      setSelectedItem(null);
      dispatch(openModal({
        title: publicBonusDetails?.result?.displayTitle || bonusDetails?.result?.displayTitle,
        content: (
          <>
            {(bonusList || publicBonusList)?.result?.bonus?.map((item, i) => {
              if (item.bonusType !== list.bonusType) return null;
              const isSel = selectedItem === item;
              return (
                <Grid item xs={1} sm={1} md={1} key={i}>
                  <Button id={`button-${item?.bonusCode}`} variant="text"
                    onClick={() => setSelectedItem(isSel ? null : item)}
                    sx={{ textDecoration: "none", color: "inherit", border: isSel ? PRIMARY : "6px solid transparent", "&:hover": { border: isSel ? PRIMARY : "6px solid transparent" }, padding: "0" }}
                  >
                    <Card variant="outlined" sx={{ bgcolor: PAPER, borderWidth: 0, padding: "0" }}>
                      <Image src={item.thumbnailUrl} alt={Constant.ALT_TAG_BONUS_IMG} />
                    </Card>
                  </Button>
                </Grid>
              );
            })}
          </>
        ),
        button: !token
          ? [
              { label: t("promotion.login"), onSubmit: () => { dispatch(closeModal()); navigate({ pathname: location.pathname, search: "?login=1" }); } },
              { label: t("promotion.register"), onSubmit: () => { dispatch(closeModal()); navigate({ pathname: location.pathname, search: "?register=1" }); } },
            ]
          : token && bonusDetails?.result?.claimable
          ? [{ label: t("promotion.claim"), onSubmit: () => { dispatch(closeModal()); handleClaimBonus(selectedItem?.bonusCode); }, disabled: !selectedItem?.isActivated }]
          : [],
      }));
      setIsClaimTrigger(true);
      setBonusType(list.bonusType);
    } else {
      navigate({ pathname: location.pathname, search: "?register=1" });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ backgroundColor: theme.palette.background.secondaryPaper, pt: { xs: "55px", sm: 0 }, pb: { xs: 8, sm: 3 } }}>

      {/* ── Tab selector — pill / segmented control ────────────────────────── */}
      <Box sx={{ backgroundColor: PAPER, borderBottom: `1px solid ${DIVIDER}` }}>
        <Box sx={{ maxWidth: { md: 960 }, mx: { md: "auto" }, px: 2, py: 1 }}>
        <Box sx={{ display: "flex", backgroundColor: "rgba(0,0,0,0.07)", borderRadius: "999px", p: "3px" }}>
          {[
            t("bonus.promotions") !== "bonus.promotions" ? t("bonus.promotions") : "Promotions",
            t("bonus.minigames") !== "bonus.minigames" ? t("bonus.minigames") : "Mini Games",
          ].map((label, i) => (
            <Box
              key={i}
              onClick={() => setActiveTab(i)}
              sx={{
                flex: 1, textAlign: "center", py: 0.9,
                fontWeight: 700, fontSize: 13, cursor: "pointer",
                borderRadius: "999px",
                backgroundColor: activeTab === i ? PAPER : "transparent",
                color: activeTab === i ? PRIMARY : MUTED,
                boxShadow: activeTab === i ? "0 1px 5px rgba(0,0,0,0.18)" : "none",
                transition: "background-color 0.2s, color 0.2s, box-shadow 0.2s",
                userSelect: "none",
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
        </Box>
      </Box>

      {(isClaimBonusLoading || isClaimBonusFetching) && <Backdrop />}

      {/* ── Two-column body ────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", maxWidth: { md: 960 }, mx: { md: "auto" }, minHeight: "calc(100vh - 99px)" }}>

        {/* Left sidebar */}
        <Box
          sx={{
            width: SIDEBAR_W,
            flexShrink: 0,
            backgroundColor: PAPER,
            borderRight: `1px solid ${DIVIDER}`,
            pt: 0.5,
          }}
        >
          {activeTab === 0
            ? publicBonusList?.result?.bonusCategory?.map((cat, idx) => {
                const active = bonusCategory === cat.code;
                return (
                  <Box
                    key={idx}
                    onClick={() => setBonusCategory(cat.code)}
                    sx={{
                      py: 1, mb: 0.5, mx: 0.5, borderRadius: 1, cursor: "pointer",
                      backgroundColor: active ? PRIMARY : "rgba(0,0,0,0.04)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      minHeight: { xs: 44, md: 60 },
                    }}
                  >
                    <Typography sx={{
                      fontSize: { xs: 8, md: 11 }, fontWeight: 700, textTransform: "uppercase",
                      textAlign: "center", lineHeight: 1.2, color: active ? "#fff" : MUTED,
                      wordBreak: "break-word", px: 0.5,
                    }}>
                      {cat.label}
                    </Typography>
                  </Box>
                );
              })
            : (gameArticles?.result || []).map((article, idx) => {
                const active = selectedArticle?.id === article.id;
                return (
                  <Box
                    key={idx}
                    onClick={() => setSelectedArticle(article)}
                    sx={{
                      mb: 0.5, mx: 0.5, borderRadius: 1, cursor: "pointer", overflow: "hidden",
                      border: `2px solid ${active ? PRIMARY : "transparent"}`,
                      background: active ? `linear-gradient(to bottom, ${lighten(PRIMARY, 0.25)}, ${PRIMARY})` : "transparent",
                      boxShadow: '1px 1px 0 rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
                      opacity: active ? 1 : 0.65,
                      transition: 'opacity 0.2s, border-color 0.2s, background 0.2s',
                    }}
                  >
                    {(article.thumbnailUrl || article.thumbnailGifUrl) && (
                      <Box component="img" src={active ? (article.thumbnailGifUrl || article.thumbnailUrl) : article.thumbnailUrl} alt={article.name} sx={{ width: "100%", display: "block" }} />
                    )}
                    <Typography sx={{ fontSize: { xs: 8, md: 11 }, fontWeight: 700, textAlign: "center", color: active ? (process.env.REACT_APP_COLOR_CONTRAST_TEXT || "white") : MUTED, py: 0.25, lineHeight: 1.2, wordBreak: "break-word" }}>
                      {article.name}
                    </Typography>
                  </Box>
                );
              })}
        </Box>

        {/* Right content */}
        <Box sx={{ flex: 1, minWidth: 0, p: 1 }}>

          {/* PROMOTIONS */}
          {activeTab === 0 && (bonusList || publicBonusList)?.result?.bonus?.map((list, index) => {
            if (!list.bonusTypeDisplay && !["DEPOSIT", "CUSTOM", "REFERRAL", "RESCUE", "RESCUE_ESPORT"].includes(list.bonusType)) return null;
            const imgSrc = list.bonusTypeDisplay ? list.bonusTypeThumbnailUrl : list.thumbnailUrl;
            return (
              <Box key={index} sx={{ mb: 1.5, borderRadius: "8px", overflow: "hidden", backgroundColor: PAPER, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                {/* Banner 16:5 */}
                <Box sx={{ position: "relative", width: "100%", paddingTop: "31.25%", backgroundColor: "#eee", overflow: "hidden" }}>
                  <Box component="img" src={imgSrc} alt={list.displayTitle}
                    sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => { e.target.parentElement.style.display = "none"; }}
                  />
                </Box>
                <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                  <Typography fontWeight={700} fontSize={13} color={PRIMARY}>{list.displayTitle}</Typography>
                  {list.description && <Typography fontSize={11} color={TEXT} sx={{ mt: 0.25, lineHeight: 1.4 }}>{list.description}</Typography>}
                </Box>
                <Box sx={{ display: "flex", gap: 1, px: 1.5, pb: 1.25, pt: 0.5 }}>
                  {/* Redemption code has no claim flow - it points players at the chat
                      group instead, shown regardless of login. Claim and Register are
                      suppressed for it below. */}
                  {list.bonusCode === REDEMPTION_CODE ? (
                    <Button variant="outlined" size="small"
                      disabled={!whatsappGroupUrl}
                      sx={{ flex: 1, fontSize: 11, fontWeight: 600, borderRadius: "6px", color: PRIMARY, borderColor: PRIMARY, "&.Mui-disabled": { borderColor: DIVIDER, color: MUTED } }}
                      onClick={() => { if (whatsappGroupUrl) window.open(whatsappGroupUrl, "_blank", "noopener,noreferrer"); }}
                    >
                      {t("promotion.joinnow")}
                    </Button>
                  ) : (
                    <>
                      {token && list.claimable && (
                        <Button variant="outlined" size="small"
                          sx={{ flex: 1, fontSize: 11, fontWeight: 600, borderRadius: "6px", color: PRIMARY, borderColor: PRIMARY, "&.Mui-disabled": { borderColor: DIVIDER, color: MUTED } }}
                          disabled={token ? !list.isActivated || isClaimBonusLoading : false}
                          onClick={() => handleClaim(list)}
                        >
                          {token ? t("promotion.claim") : t("promotion.register")}
                        </Button>
                      )}
                      {!token && (
                        <Button variant="outlined" size="small"
                          sx={{ flex: 1, fontSize: 11, fontWeight: 600, borderRadius: "6px", color: PRIMARY, borderColor: PRIMARY }}
                          onClick={() => navigate({ pathname: location.pathname, search: "?register=1" })}
                        >
                          {t("promotion.register")}
                        </Button>
                      )}
                    </>
                  )}
                  <Button variant="contained" size="small"
                    sx={{ flex: 1, fontSize: 11, fontWeight: 600, borderRadius: "6px", backgroundImage: `linear-gradient(to bottom, ${lighter} 50%, ${darker} 50%)`, backgroundSize: "100% 100%" }}
                    onClick={() => handleReadMore(list)}
                  >
                    {t("promotion.readmore")}
                  </Button>
                </Box>
              </Box>
            );
          })}

          {/* MINI GAMES */}
          {activeTab === 1 && (() => {
            if (!selectedArticle) return (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography color={MUTED} fontSize={13}>
                  {t("minigame.selectarticle") !== "minigame.selectarticle" ? t("minigame.selectarticle") : "Select a game from the menu"}
                </Typography>
              </Box>
            );
            let imgs = [];
            try { imgs = JSON.parse(selectedArticle.imageUrls || "[]"); } catch { /* */ }
            return (
              <Box>
                {selectedArticle.bannerUrl && (
                  <Box sx={{ position: "relative", width: "100%", paddingTop: "31.25%", borderRadius: "8px", overflow: "hidden", backgroundColor: "#eee", mb: 1.5 }}>
                    <Box component="img" src={selectedArticle.bannerUrl} alt={selectedArticle.name}
                      sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                  </Box>
                )}
                <Typography fontWeight={700} fontSize={16} color={TEXT} mb={1}>{selectedArticle.name}</Typography>
                {selectedArticle.content && (
                  <Box sx={{ fontSize: 13, color: TEXT, lineHeight: 1.6, mb: 1.5, "& p": { mt: 0, mb: 0.75 } }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedArticle.content) }} />
                )}
                {imgs.length > 0 && (
                  <Box sx={{
                    display: "flex", gap: 1, overflowX: "auto", mb: 1.5,
                    scrollbarWidth: { xs: "none", sm: "thin" },
                    "&::-webkit-scrollbar": { height: { xs: 0, sm: "6px" } },
                    "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.2)", borderRadius: "3px" },
                  }}>
                    {imgs.map((url, i) => (
                      <Box key={i} component="img" src={url} sx={{
                        width: { xs: "calc(50vw - 40px)", sm: "160px" },
                        flexShrink: 0,
                        borderRadius: "6px",
                      }} />
                    ))}
                  </Box>
                )}
                <Box sx={{ mb: 1.5 }}>
                  {!token
                    ? <Button variant="outlined" fullWidth sx={{ borderRadius: "8px", fontWeight: 700, py: 1.25, color: PRIMARY, borderColor: PRIMARY }} onClick={() => navigate({ pathname: location.pathname, search: "?register=1" })}>
                        {t("promotion.register")}
                      </Button>
                    : selectedArticle.link
                      ? <Button variant="contained" fullWidth sx={{ borderRadius: "8px", fontWeight: 700, py: 1.25, backgroundColor: PRIMARY, "&:hover": { backgroundColor: darken(PRIMARY, 0.1) } }} onClick={() => window.open(selectedArticle.link, "_self")}>
                          {t("minigame.playnow") !== "minigame.playnow" ? t("minigame.playnow") : "Play Now"}
                        </Button>
                      : <Button variant="contained" fullWidth disabled sx={{ borderRadius: "8px", fontWeight: 700, py: 1.25, "&.Mui-disabled": { backgroundColor: PRIMARY, color: "#fff", opacity: 0.6 } }}>
                          {t("minigame.comingsoon") !== "minigame.comingsoon" ? t("minigame.comingsoon") : "Coming Soon"}
                        </Button>
                  }
                </Box>
                {selectedArticle.termsAndConditions && (
                  <Accordion sx={{ backgroundColor: PAPER, boxShadow: "none", border: `1px solid ${DIVIDER}`, borderRadius: "8px !important", "&:before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: TEXT }} />}>
                      <Typography fontWeight={600} fontSize={13} color={TEXT}>
                        {t("minigame.termsandconditions") !== "minigame.termsandconditions" ? t("minigame.termsandconditions") : "Terms And Conditions"}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ fontSize: 12, color: TEXT, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedArticle.termsAndConditions) }} />
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            );
          })()}

        </Box>
      </Box>
    </Box>
  );
};

export default Bonus_v2;
